import {
  DateTimeFormatter,
  getCurrentUserHook,
  withErrorBoundary,
  AttachmentPreviewer,
  getDownloadURL,
  ALL_MODULES,
  useIsAuthorisedHook,
  getTranslatedText,
  toast,
  useIntl,
  useUserMSPCheckHook,
  generateString,
  TICKET_SOURCE,
  getParsedCrudData
} from '@nile/components/src';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  TextStyle,
  Popover,
  Modal,
  TextContainer,
  Badge,
  Card
} from '@superops/ui-design-system';
import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef
} from 'react';
import AttachmentHolder from '@nile/rmm/src/modules/common/components/sub-pages/attachment-holder';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  SPLIT_TICKET,
  UPDATE_NOTE_WORKITEM,
  DELETE_NOTE
} from '@nile/graphql/src';
import { useMutation } from '@apollo/client';
import SplitSheet from '@nile/mangifera/src/Components/Sheets/SplitSheet';
import envConfig from '@nile/home/src/Config/env';
import { EditNoteContainer } from '@nile/mangifera/src/Containers/ChatContainers';
import { FormattedMessage } from 'react-intl';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import { useTicketDetailsConversationStore } from '@nile/mangifera/src/StoreManager/store/ticketDetailsConversationStore';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
const NoteBubbleNew = (props) => {
  const { data, ticketId, client, readOnly, ticketSource } = props;
  const intl = useIntl();

  const editorConfig = {
    id: ticketId,
    name: `editNote_${ticketId}`,
    cacheConfig: {
      scope: 'global'
    }
  };

  const { template, updateTemplate, resetTemplate } = useSuperEditor(
    editorConfig
  );

  const currentUser = getCurrentUserHook();
  const message = data?.message;
  const isMyChat = currentUser?.userId === data?.user?.userId;
  let initial = null;
  const addedUser =
    data?.user?.name ?? getTranslatedText(intl, 'common.user.system');
  const tabContext = useContext(TicketContext);
  const isMSP = useUserMSPCheckHook();

  if (data?.user) {
    const firstLetter = data?.user?.firstName?.[0];
    initial = firstLetter;
  }

  const htmlData = data?.note?.content;

  let htmlToDisplay = dompurifier(htmlData, {
    FORBID_TAGS: ['style']
  });

  htmlToDisplay = htmlToDisplay.replaceAll('<img', "<img loading='lazy'");

  const isPublicNote = data?.note?.privacyType === 'PUBLIC';

  // Attachment preview functions

  const attachments = data?.note?.attachments;

  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });
  const [formatterAttachment, setFormatterAttachment] = useState([]);

  const getAttachmentURL = (item) => {
    const module = ALL_MODULES.ticketNoteAttachment;

    const details = {
      fileName: item?.fileName,
      noteId: data?.note?.noteId,
      clientId: client?.accountId,
      ticketId: ticketId
    };

    const url = getDownloadURL({ module, details });
    return url;
  };

  function formatAttachmentForPreview(attachments, cb) {
    const formattedItems = attachments.map((item) => {
      if (item)
        return {
          name: item.originalFileName,
          url: getAttachmentURL(item),
          type: item.fileName.substr(
            item.fileName.lastIndexOf('.') + 1,
            item.fileName.length
          )
        };
    });
    setFormatterAttachment(formattedItems);
    cb();
  }

  function openAttachmentPreview(index, attachments, item) {
    if (attachments) {
      formatAttachmentForPreview(attachments, () => {
        setShowAttachmentPreview({
          ...showAttachmentPreview,
          show: true,
          index
        });
      });
    }
  }

  const closePreviwer = () => {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: false
    });
  };

  //END: Attachment preview functions

  const isNoteEditDeleteAuthorised =
    useIsAuthorisedHook('ticketingAddNotes.allow') && isMyChat;

  //Instant messaging shouldn't have split ticket option
  const isTicketSplitAuthorised =
    useIsAuthorisedHook('splitTicket.allow') &&
    ticketSource !== TICKET_SOURCE.Communication;

  // Actions

  const [actionPopoverActive, setActionPopoverActive] = useState(false);

  const togglePopOver = useCallback(() => {
    setActionPopoverActive(!actionPopoverActive);
  }, [actionPopoverActive]);

  // SPLIT TICKET OPERATIONS
  const [splitTickets] = useMutation(SPLIT_TICKET, {
    onCompleted(res) {
      console.log(res);
    },
    onError(res) {
      console.log(res);
    }
  });

  const openSplitTicket = useCallback((res) => {
    const ticketData = res.data.splitTicket;
    const ticketId = ticketData?.ticketId;
    tabContext.openTab({
      module: 'TICKET',
      ticketId: ticketId,
      data: ticketData
    });
  }, []);

  const splitTicket = useCallback(
    (subject, copyFields) => {
      splitTickets({
        variables: {
          ticketId: ticketId,
          noteId: data?.note?.noteId,
          subject,
          copyFields
        }
      }).then((res) => {
        openSplitTicket(res);
      });
    },
    [data, ticketId]
  );

  // END ACTIONS

  // Edit note
  const editNoteData = useTicketDetailsConversationStore((s) => s.editNoteData);
  const setEditNoteData = useTicketDetailsConversationStore(
    (s) => s.setEditNoteData
  );

  const editNotePrivacyType = useTicketDetailsConversationStore(
    (s) => s.editNotePrivacyType
  );
  const setEditNotePrivacyType = useTicketDetailsConversationStore(
    (s) => s.setEditNotePrivacyType
  );

  const resetEditNote = useTicketDetailsConversationStore(
    (s) => s.resetEditNote
  );

  const editStateRef = useRef(false);
  editStateRef.current = editNoteData?.noteId === data?.note?.noteId;
  const [deleteModalActive, setDeleteModalActive] = useState(false);

  const handleEditNote = () => {
    setEditNoteData(data?.note);
    const template = {
      attachments: data?.note?.attachments || [],
      content: data?.note?.content || '',
      inlineImages: data?.note?.inlineImages || [],
      mentions: data?.note?.mentions || []
    };
    updateTemplate(template);
    setEditNotePrivacyType(data?.note?.privacyType);
    setActionPopoverActive(false);
  };

  const [
    updateNote,
    { loading: editNoteLoading, error: editNoteError }
  ] = useMutation(UPDATE_NOTE_WORKITEM, {
    update(cache, { data }) {
      const updatedNote = data?.updateNote;
      if (!updatedNote) return;
      cache.modify({
        fields: {
          getTicketAuditList(existing = {}, { readField }) {
            const existingFeeds = existing.ticketAudit ?? [];
            const updatedFeeds = existingFeeds.map((feedRef) => {
              const note = readField('note', feedRef);
              const noteId = note && readField('noteId', note);
              if (noteId === updatedNote.noteId) {
                return {
                  ...feedRef,
                  note: {
                    __typename: 'Note',
                    ...updatedNote
                  }
                };
              }
              return feedRef;
            });
            return {
              ...existing,
              ticketAudit: updatedFeeds
            };
          }
        }
      });
    },
    onCompleted() {
      resetEditNote();
      resetTemplate();
      toast('success', getTranslatedText(intl, 'ticket.note.edit.success'));
    }
  });

  const saveNote = async (params) => {
    const base = data?.note?.attachments || [];
    const updatedAttachments = template?.attachments;
    const { addedItems, deletedItems: deletedAttachments } = getParsedCrudData(
      base,
      updatedAttachments,
      'fileName'
    );

    const addAttachments = addedItems?.length
      ? addedItems.map(({ fileName, originalFileName, fileSize }) => ({
          fileName,
          originalFileName,
          fileSize
        }))
      : [];

    const variables = {
      variables: {
        input: {
          noteId: data?.note?.noteId,
          content: dompurifier(template?.content),
          mentions: template?.mentions,
          inlineImages: template?.inlineImages,
          addAttachments: addAttachments,
          deleteAttachments: deletedAttachments,
          privacyType: params?.variables?.input?.privacyType
        }
      }
    };
    const res = await updateNote(variables);
  };

  const messageProp = {
    data: {
      noteId: data?.note?.noteId,
      ticketId: ticketId
    }
  };

  // End edit note

  // Delete note

  const [deleteNote, { loading: deleteNoteLoading }] = useMutation(
    DELETE_NOTE,
    {
      update(cache, { data }) {
        const deletedNoteId = data?.deleteNote;
        if (!deletedNoteId) return;

        cache.modify({
          fields: {
            getTicketAuditList(existing = {}, { readField }) {
              const existingFeeds = existing.ticketAudit ?? [];

              const filteredFeeds = existingFeeds.filter((feedRef) => {
                const note = readField('note', feedRef);
                const noteId = note && readField('noteId', note);
                return noteId !== deletedNoteId;
              });

              // If nothing changed, avoid unnecessary write
              if (filteredFeeds.length === existingFeeds.length) {
                return existing;
              }

              return {
                ...existing,
                ticketAudit: filteredFeeds
              };
            }
          }
        });
      },

      onCompleted() {
        toast('success', getTranslatedText(intl, 'ticket.note.delete.success'));
      }
    }
  );

  const handleDeleteNote = () => {
    deleteNote({
      variables: {
        input: {
          noteId: data?.note?.noteId
        }
      }
    });
  };

  function handleCancelEditNote() {
    resetEditNote();
    resetTemplate();
  }

  // End Delete note

  // Inline images - preview handler, for images in notes html content
  const handleEscPress = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (e?.keyCode === 27 || e?.which === 27 || e?.key === 'Escape') {
      if (
        window.getComputedStyle(document.querySelector(`.modal-inline-images`))
          .display === 'flex'
      ) {
        document.querySelector(`.modal-inline-images`).style.display = 'none';
      }
      window.removeEventListener('keyup', handleEscPress);
    }
  };

  const imageDownloader = (e) => {
    // window.open(e.target.currentSrc, '_blank').focus();
    const modal = document.querySelector(`.modal-inline-images`);

    const modalImg = document.querySelector(`.modal-content-inline-images`);

    modal.style.display = 'flex';
    modalImg.src = e.target.currentSrc;
    window.addEventListener('keyup', handleEscPress);
  };

  function updatePrivacyType(bool) {
    const updatedPrivacyType = bool ? 'PUBLIC' : 'PRIVATE';
    setEditNotePrivacyType(updatedPrivacyType);
  }

  const randomId = generateString(5) || 'abcde';

  useEffect(() => {
    if (
      document.querySelector(`.${randomId}`)?.querySelectorAll('img')?.length
    ) {
      document
        ?.querySelector(`.${randomId}`)
        ?.querySelectorAll('img')
        ?.forEach((img) => {
          img.classList.add('cursor-zoom-in', 'mw100');
          img.addEventListener('click', imageDownloader);
        });
      return () => {
        document
          ?.querySelector(`.${randomId}`)
          ?.querySelectorAll('img')
          ?.forEach((img) => {
            img.classList.remove('cursor-zoom-in');
            img.removeEventListener('click', imageDownloader);
          });
      };
    }
  }, []);

  const noteBubbleWrapperClassNames = [
    'flex g10 bubble-wrapper',
    isMyChat && 'frow-reverse',
    'px10'
  ]
    .filter(Boolean)
    .join(' ');

  const bubbleOuterClassNames = [
    'w100',
    (editNoteLoading || deleteNoteLoading) && 'opacity-half'
  ]
    .filter(Boolean)
    .join(' ');

  const bubblePadding = 'pt20 pb20 px10';

  const noteBubbleClassNames = ['word-break br20', bubblePadding]
    .filter(Boolean)
    .join(' ');

  const headerClassNames = ['bubble-header fsize12 flex actr fx-sb mb-10 fg1']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={noteBubbleWrapperClassNames}>
      <div id="bubble-outer" className={bubbleOuterClassNames}>
        <Card
          variant="attention"
          padding="sizeExtraSmall"
          border={
            editStateRef.current ? 'primary' : isPublicNote ? 'success' : 'none'
          }
        >
          <div id="note-bubble-inner" className={noteBubbleClassNames}>
            <div className="flex g10 align-items-center">
              <div className="mr8">
                <Avatar size="extraSmall" initials={initial} />
              </div>
              <div className="fw">
                <div className={headerClassNames}>
                  <div className="added-by flex g5 actr">
                    {isPublicNote ? (
                      <>
                        <Badge
                          size="large"
                          icon={<SVGIcon name="Notes" size="sm" />}
                          iconPosition="left"
                          status="success"
                        >
                          <div>
                            <TextStyle weight="Medium">
                              <FormattedMessage id="common.attribute.note.public" />
                            </TextStyle>
                          </div>
                        </Badge>

                        <div className="flex g3">
                          <div>
                            <TextStyle>
                              <FormattedMessage id="common.keyword.by" />
                            </TextStyle>
                          </div>
                          <div>
                            <TextStyle variation="strong">
                              {addedUser}
                            </TextStyle>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex">
                          <SVGIcon name="Notes" size={'sm'} />
                        </div>
                        <div className="flex g3">
                          <div>
                            <TextStyle>
                              <FormattedMessage id="ticket.details.chat.noteby" />
                            </TextStyle>
                          </div>
                          <div>
                            <TextStyle variation="strong">
                              {addedUser}
                            </TextStyle>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex g3 right-container flex actr">
                    <TextStyle
                      variation="subdued"
                      title={
                        <DateTimeFormatter type="dateTime" date={data?.time} />
                      }
                    >
                      <DateTimeFormatter type="dateTime" date={data?.time} />
                    </TextStyle>

                    {isMSP &&
                      (isNoteEditDeleteAuthorised || isTicketSplitAuthorised) &&
                      !readOnly && (
                        <span
                          className="pl-4 pointer flex actr"
                          id="bubble-actions"
                        >
                          <Popover
                            activator={
                              <div onClick={togglePopOver} className="ptr flex">
                                <SVGIcon name="Menu" size="sm" />
                              </div>
                            }
                            active={actionPopoverActive}
                            preferredAlignment="right"
                            onClose={() => {
                              togglePopOver();
                            }}
                          >
                            <div className="flex col g15 p10">
                              {isNoteEditDeleteAuthorised && (
                                <>
                                  {!props?.editNoteId && (
                                    <div
                                      className="flex g5 pointer"
                                      onClick={handleEditNote}
                                    >
                                      <div className="flex">
                                        <SVGIcon name="Edit" />
                                      </div>
                                      <div>
                                        <FormattedMessage id="common.edit" />
                                      </div>
                                    </div>
                                  )}
                                  <Modal
                                    activator={
                                      <div
                                        className="flex g5 pointer"
                                        onClick={() =>
                                          setDeleteModalActive(true)
                                        }
                                      >
                                        <div className="flex">
                                          <SVGIcon name="Delete" />
                                        </div>

                                        <div>
                                          <FormattedMessage id="common.delete" />
                                        </div>
                                      </div>
                                    }
                                    open={deleteModalActive}
                                    onClose={() => setDeleteModalActive(false)}
                                    variant="confirmation"
                                    title={getTranslatedText(
                                      intl,
                                      'ticket.note.delete.confirmation.title'
                                    )}
                                    primaryAction={{
                                      content: getTranslatedText(
                                        intl,
                                        'common.delete'
                                      ),
                                      onAction: handleDeleteNote,
                                      destructive: true
                                    }}
                                    secondaryActions={[
                                      {
                                        content: getTranslatedText(
                                          intl,
                                          'common.cancel'
                                        ),
                                        onAction: () =>
                                          setDeleteModalActive(false)
                                      }
                                    ]}
                                  >
                                    <Modal.Section>
                                      <TextContainer>
                                        <FormattedMessage id="ticket.note.delete.confirmation.message" />
                                      </TextContainer>
                                    </Modal.Section>
                                  </Modal>
                                </>
                              )}
                              {isTicketSplitAuthorised && (
                                <SplitSheet splitTicket={splitTicket} />
                              )}
                            </div>
                          </Popover>
                        </span>
                      )}
                  </div>
                </div>

                {!editStateRef.current ? (
                  <>
                    <p
                      // className="message-content"
                      className={`message-content ${randomId}`}
                      dangerouslySetInnerHTML={{
                        __html: htmlToDisplay
                      }}
                    ></p>

                    {attachments?.length > 0 && (
                      <div className="flex wrap mt-10 g10 attachments-container">
                        {attachments.map((item, index) => (
                          <AttachmentHolder
                            onClick={() =>
                              openAttachmentPreview(index, attachments, item)
                            }
                            fileName={item?.originalFileName || ''}
                            fontWeight="bolder"
                            padded
                          />
                        ))}
                      </div>
                    )}

                    {showAttachmentPreview.show && (
                      <AttachmentPreviewer
                        showPreviewer={showAttachmentPreview.show}
                        closePreviwer={closePreviwer}
                        list={formatterAttachment}
                        selectedIndex={showAttachmentPreview.index}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <EditNoteContainer
                      attachmentUploadUrl={`${envConfig.fileRoot}/upload`}
                      allowMentions
                      metadata={{ module: 'TICKET_NOTE' }}
                      module="NOTE"
                      details={{
                        workItem: {
                          module: ALL_MODULES.ticket,
                          workId: ticketId
                        }
                      }}
                      saveState
                      uploadParams={{
                        module: ALL_MODULES.ticketNoteAttachment,
                        details: JSON.stringify({
                          ticketId: ticketId
                        })
                      }}
                      message={messageProp}
                      saveNote={saveNote}
                      setEditNoteId={() => {}}
                      editNoteLoading={editNoteLoading}
                      disabled={editNoteLoading}
                      onCancel={handleCancelEditNote}
                      privacyType={editNotePrivacyType}
                      updatePrivacyType={updatePrivacyType}
                      isSuperEditor
                      editorConfig={editorConfig}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default withErrorBoundary(NoteBubbleNew);
